import type { UnifiedAIRequest } from '@quipex/shared/contracts';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
    toQuippyDocumentOptions,
    toQuippyResponseContent,
    toQuippySources,
} from '../mapper';
import type { QuippyBuildingSession, QuippyScope } from '../models';
import type { QuippyApi } from '../transport';
import { updateAssistantMessageInSession } from './state';

export interface AssistantRequest {
    api: QuippyApi;
    scope: QuippyScope;
    chatId: string;
    messageId: string;
    payload: UnifiedAIRequest;
    retryInput: string;
    documentSelectionMessageId?: string;
    onError?: () => void;
    updateSession: (
        scope: QuippyScope,
        updater: (session: QuippyBuildingSession) => QuippyBuildingSession
    ) => void;
}

export function processAssistantRequest$(request: AssistantRequest) {
    return request.api.ask(request.payload).pipe(
        tap((response) => {
            const selectedFileId = request.payload.selectedFileId;
            request.updateSession(request.scope, (session) =>
                updateAssistantMessageInSession(
                    session,
                    request.chatId,
                    request.messageId,
                    {
                        packAction: response.packAction,
                        content: toQuippyResponseContent(response),
                        status: 'sent',
                        sources: toQuippySources(response.files ?? []),
                        documentOptions: toQuippyDocumentOptions(response),
                        retryInput: selectedFileId
                            ? request.retryInput
                            : undefined,
                        retrySelectedFileId: selectedFileId,
                    }
                )
            );
            const documentSelectionMessageId =
                request.documentSelectionMessageId;
            if (selectedFileId && documentSelectionMessageId) {
                request.updateSession(request.scope, (session) =>
                    updateAssistantMessageInSession(
                        session,
                        request.chatId,
                        documentSelectionMessageId,
                        {
                            selectedDocumentFileId: selectedFileId,
                            documentSelectionStatus: 'selected',
                        }
                    )
                );
            }
        }),
        catchError(() => {
            if (request.onError) {
                request.onError();
                return of(undefined);
            }

            request.updateSession(request.scope, (session) =>
                updateAssistantMessageInSession(
                    session,
                    request.chatId,
                    request.messageId,
                    {
                        content: 'Something went wrong. Please try again.',
                        status: 'error',
                        sources: [],
                        retryInput: request.retryInput,
                        retrySelectedFileId: request.payload.selectedFileId,
                    }
                )
            );

            return of(undefined);
        }),
        map(() => undefined)
    );
}
